<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import { cheapest, forPeriod, type LineQuote, type ProviderQuote } from '../model/cost'
import { PRICES } from '../model/priceBook'
import { PERIODS, type Period, type Resource, type Spec } from '../model/spec'

/** Every resource on every provider, for the period the visitor picks. */
const props = defineProps<{
  spec: Spec
  /** `null` while a field is unusable. */
  quotes: readonly ProviderQuote[] | null
}>()
const emit = defineEmits<{ 'update:period': [Period] }>()

const { t, n } = useI18n()
const { money } = useFormat()

const amount = (monthly: number): string =>
  money(forPeriod(monthly, props.spec.period, PRICES.hoursPerMonth), props.spec.period)

interface Row {
  readonly key: string
  readonly name: string
  readonly summary: string
  /** Requested size, to tell when a provider had to round up. */
  readonly size: { readonly vcpu: number; readonly memoryGb: number } | null
  readonly count: number
  readonly cells: readonly (LineQuote | null)[]
  readonly lowest: Set<number>
}

function summarise(resource: Resource): string {
  const count = n(resource.kind === 'bucket' ? 1 : resource.count)
  switch (resource.kind) {
    case 'vm':
    case 'db': {
      const machine = t('summary.machine', { vcpu: n(resource.vcpu), memory: n(resource.memoryGb) })
      return resource.kind === 'vm'
        ? t('summary.vm', {
            count,
            machine,
            os: t(`os.${resource.os}`),
            hours: n(resource.hoursPerDay),
          })
        : t('summary.db', {
            count,
            machine,
            storage: n(resource.storageGb),
            availability: t(resource.ha ? 'summary.ha' : 'summary.single'),
          })
    }
    case 'disk':
      return t('summary.disk', { count, size: n(resource.sizeGb) })
    case 'bucket':
      return t('summary.bucket', {
        size: n(resource.sizeGb),
        storageClass: t(`storageClass.${resource.storageClass}`),
      })
    case 'lb':
      return t('summary.lb', { count })
  }
}

/** Marked only when it stands out: a row where every provider ties has no winner. */
const isLowest = (row: Row, index: number): boolean =>
  row.lowest.has(index) && row.lowest.size < row.cells.length

const lowestOf = (cells: readonly (LineQuote | null)[]): Set<number> =>
  cheapest(cells.map((cell) => cell?.monthly ?? null))

const rows = computed<Row[]>(() => {
  const quotes = props.quotes ?? []
  const resources = props.spec.resources.map((resource, index) => {
    const cells = quotes.map((quote) => quote.lines[index] ?? null)
    return {
      key: String(resource.id),
      name: t(`kind.${resource.kind}`),
      summary: summarise(resource),
      size:
        resource.kind === 'vm' || resource.kind === 'db'
          ? { vcpu: resource.vcpu, memoryGb: resource.memoryGb }
          : null,
      count: resource.kind === 'bucket' ? 1 : resource.count,
      cells,
      lowest: lowestOf(cells),
    }
  })
  const transfer = quotes.map((quote) => quote.transfer)
  return [
    ...resources,
    {
      key: 'transfer',
      name: t('kind.transfer'),
      summary: t('summary.transfer', {
        inbound: n(props.spec.transfer.inboundGb),
        outbound: n(props.spec.transfer.outboundGb),
      }),
      size: null,
      count: 1,
      cells: transfer,
      lowest: lowestOf(transfer),
    },
  ]
})

const discounted = computed(() => (props.quotes ?? []).some((quote) => quote.discountMonthly > 0))
const lowestTotal = computed(() =>
  cheapest((props.quotes ?? []).map((quote) => (quote.complete ? quote.netMonthly : null))),
)

/** What the cell is priced as: "2 × m7i.xlarge", or the product's name. */
function label(row: Row, cell: LineQuote): string | null {
  if (cell.machine)
    return row.count > 1 ? `${n(row.count)} × ${cell.machine.name}` : cell.machine.name
  return cell.product
}

/** The machine's size, only when it is larger than asked: the reason a price is higher. */
function upsized(row: Row, cell: LineQuote): string | null {
  const { machine } = cell
  if (!machine || !row.size) return null
  if (machine.vcpu === row.size.vcpu && machine.memoryGb === row.size.memoryGb) return null
  return t('summary.machine', { vcpu: n(machine.vcpu), memory: n(machine.memoryGb) })
}

function onPeriod(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLInputElement)) return
  const period = PERIODS.find((candidate) => candidate === target.value)
  if (period) emit('update:period', period)
}
</script>

<template>
  <section class="panel breakdown" aria-labelledby="breakdown-heading">
    <div class="breakdown__head">
      <h2 id="breakdown-heading" class="panel__title">{{ t('breakdown.heading') }}</h2>

      <fieldset class="period">
        <legend class="visually-hidden">{{ t('breakdown.period') }}</legend>
        <label v-for="period in PERIODS" :key="period" class="period__option">
          <input
            type="radio"
            name="period"
            :value="period"
            :checked="spec.period === period"
            @change="onPeriod"
          />
          <span>{{ t(`breakdown.${period}`) }}</span>
        </label>
      </fieldset>
      <InfoTip :label="t('breakdown.period')" :text="t('breakdown.periodHelp')" />
    </div>

    <div v-if="quotes" class="breakdown__scroll">
      <table data-testid="breakdown">
        <thead>
          <tr>
            <th scope="col">{{ t('breakdown.resource') }}</th>
            <th v-for="quote in quotes" :key="quote.provider" scope="col">
              {{ PRICES.providers[quote.provider].name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.key">
            <th scope="row">
              <span class="breakdown__name">{{ row.name }}</span>
              <span class="breakdown__summary">{{ row.summary }}</span>
            </th>
            <td
              v-for="(cell, index) in row.cells"
              :key="index"
              :class="{ 'breakdown__cell--lowest': isLowest(row, index) }"
            >
              <template v-if="cell">
                <span class="breakdown__amount mono">{{ amount(cell.monthly) }}</span>
                <span v-if="isLowest(row, index)" class="visually-hidden">
                  {{ t('breakdown.cheapest') }}
                </span>
                <span v-if="label(row, cell)" class="breakdown__sku">{{ label(row, cell) }}</span>
                <span v-if="upsized(row, cell)" class="breakdown__sku">{{
                  upsized(row, cell)
                }}</span>
              </template>
              <span v-else class="breakdown__missing">{{ t('breakdown.noMatch') }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <template v-if="discounted">
            <tr>
              <th scope="row">{{ t('breakdown.list') }}</th>
              <td v-for="quote in quotes" :key="quote.provider" class="mono">
                {{ amount(quote.listMonthly) }}
              </td>
            </tr>
            <tr>
              <th scope="row">{{ t('breakdown.discount') }}</th>
              <td v-for="quote in quotes" :key="quote.provider" class="mono">
                {{ quote.discountMonthly > 0 ? `−${amount(quote.discountMonthly)}` : '—' }}
              </td>
            </tr>
          </template>
          <tr class="breakdown__total">
            <th scope="row">{{ t('breakdown.total') }}</th>
            <td
              v-for="(quote, index) in quotes"
              :key="quote.provider"
              class="mono"
              :class="{ 'breakdown__cell--lowest': lowestTotal.has(index) }"
            >
              {{ amount(quote.netMonthly) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <p v-else class="panel__empty">{{ t('totals.empty') }}</p>
  </section>
</template>

<style scoped>
.breakdown__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
}

.breakdown__head .panel__title {
  margin-right: auto;
}

/* A segmented control built from real radio buttons: arrow keys move between them. */
.period {
  display: inline-flex;
  margin: 0;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.period__option {
  position: relative;
}

.period__option input {
  position: absolute;
  opacity: 0;
  inset: 0;
  margin: 0;
  cursor: pointer;
}

.period__option span {
  display: block;
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.period__option + .period__option span {
  border-left: 1px solid var(--color-border);
}

.period__option input:checked + span {
  background: var(--color-accent-subtle);
  color: var(--color-accent);
  font-weight: 600;
}

.period__option input:focus-visible + span {
  outline: 2px solid var(--color-focus-ring);
  outline-offset: -2px;
}

/* The table may be wider than a phone; it scrolls, the page does not. */
.breakdown__scroll {
  overflow-x: auto;
  margin-top: var(--space-4);
}

table {
  width: 100%;
  min-width: 40rem;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

th,
td {
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--color-border);
  text-align: right;
  vertical-align: top;
}

th:first-child {
  text-align: left;
}

thead th {
  color: var(--color-text-muted);
  font-weight: 500;
  white-space: nowrap;
}

tbody th {
  font-weight: 400;
}

.breakdown__name {
  display: block;
  font-weight: 600;
}

.breakdown__summary,
.breakdown__sku {
  display: block;
  color: var(--color-text-muted);
}

.breakdown__amount {
  display: block;
  font-size: var(--text-base);
  white-space: nowrap;
}

.breakdown__sku {
  font-size: 0.75rem;
}

.breakdown__missing {
  color: var(--color-warning);
}

.breakdown__cell--lowest .breakdown__amount,
.breakdown__total .breakdown__cell--lowest {
  color: var(--color-success);
  font-weight: 600;
}

tfoot th,
tfoot td {
  white-space: nowrap;
}

.breakdown__total th,
.breakdown__total td {
  border-bottom: 0;
  font-size: var(--text-base);
  font-weight: 600;
}
</style>
